import React, { useEffect, useState } from 'react';
import './TransactionPage.css';
import { ChevronLeft, ChevronRight, SquareSigma, BookOpen, BookLock, Newspaper  } from 'lucide-react';
import {Card, CardContent, StatCard} from "../../components/card-components";
import DashboardBase  from "../DashboardBase";
import {formatCurrency, formatDateIntl} from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import StatusFilterComponent from '../../components/StatusFilterComponent';
import SearchComponent from '../../components/SearchComponent';
import UserSearch from '../../components/UserSearch';
import { useNavigate } from "react-router";
import {generateDateRanges} from "../../util/date_generate";
import { useAuth } from '../../context/AuthContext';
import {ROLES} from "../../config/routes";

const TransactionPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState({
    transactions: true,
    stats: true,
  });
  const [error, setError] = useState(null);
  const itemsPerPage = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('GBP');
  const [timePeriod, setTimePeriod] = useState("lastSixMonths");
  const [selectedFund, setSelectedFund] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const isLoading = Object.values(loading).some(status => status);
  const { isLoggedIn, userRole, login, logout } = useAuth();

  const navigate = useNavigate();

  useEffect((isLoading) => {
    // Fetch the transaction data from the backend
    if (!isLoading) {
      fetchTransactions(currentPage, selectedFund, selectedStatus, selectedUser);
      fetchStats(selectedCurrency, timePeriod);
    }
  }, [currentPage]);

  useEffect(() => {
    // Fetch the transaction data from the backend
      fetchStats(selectedCurrency, timePeriod);
  }, [timePeriod]);

  const handleFundSelect = (fund) => {
    // Call your fetchTransactions with the selected fund
    setSelectedFund(fund);
    setCurrentPage(1)
    fetchTransactions(1, fund, selectedStatus, selectedUser);
  };

  const handleUserSelect = (user) => {
    // Call your fetchTransactions with the selected fund
    setSelectedUser(user);
    setCurrentPage(1)
    fetchTransactions(1, selectedFund, selectedStatus, user);
  };

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    setCurrentPage(1);
    fetchTransactions(1, selectedFund, newStatus, selectedUser);
  };

  const fetchTransactions = (page, fund, status, user) => {
    const token = localStorage.getItem('token');
    const indexOfLastTransaction = ((page - 1) * itemsPerPage);

    let url = `${process.env.REACT_APP_API_URL}/api/v1/transactions?offset=${indexOfLastTransaction}&limit=${itemsPerPage}`
    if (fund) {
      url += `&fund_id=${encodeURIComponent(fund.id)}`;
    }
    if (user) {
      url += `&user_id=${encodeURIComponent(user.id)}`;
    }
    if (status !== 'all') {
      url += `&status=${encodeURIComponent(status)}`;
    }

    fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setTransactions(data.rows);
        setTotal(data.total);
        setLoading(prev => ({ ...prev, transactions: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, transactions: false }));
      });
  };

  const fetchStats= (targetCurrency, timePeriod) => {
    const token = localStorage.getItem('token');

    if (!loading.stats) {
      setLoading(prev => ({ ...prev, stats: true }));
    }

    let url = `${process.env.REACT_APP_API_URL}/api/v1/transactions-overview/${targetCurrency}`;
    if (timePeriod) {
      let filter = generateDateRanges();
      let dates = filter[timePeriod]
      url += `?&start_date=${encodeURIComponent(dates.start)}&end_date=${encodeURIComponent(dates.end)}`;
    }

    fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setStats(data);
        setLoading(prev => ({ ...prev, stats: false }));
      })
      .catch((error) => {
        setError(error);
        setLoading(prev => ({ ...prev, stats: false }));
      });
  };

  if (isLoading) {
    <Loader loading={isLoading} />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'status-completed';
      case 'completed': return 'status-pending';
      case 'failed': return 'status-rejected';
      default: return 'status-default';
    }
  };

  const getSideClass = (status) => {
    switch (status.toLowerCase()) {
      case 'buy': return 'status-completed';
      case 'sell': return 'status-pending';
      default: return 'status-default';
    }
  };

  const goToItem = (id) => {
    navigate(`/transaction-details/${id}`);
  };

  return (
    <DashboardBase
      page="transactions"
      content= {
            <div className="dashboard-home">
                {/* Secondary Navigation */}

                <div className="dashboard-header">
                        <h1>Summary</h1>

                        <select
                            value={timePeriod} 
                            onChange={(e) => setTimePeriod(e.target.value)}
                            className="select-control">
                                <option value={"thisWeek"} >This week</option>
                                <option value={"thisMonth"} >This month</option>
                                <option value={"lastSixMonths"} >Last six months</option>
                                <option value={"lastYear"} >Last year</option>
                          </select>

                </div>

                {/* Stats Grid */}
                <div className="stats-grid">
                  <div className="stats-container">
                      <StatCard 
                        title="Total" 
                        value={stats.total_transactions} 
                        icon={() => <div className="text-xs"> <SquareSigma/>  </div>}
                      />
                      <StatCard 
                        title="SOLD" 
                        value={stats.sell_count} 
                        icon={() => <div className="text-xs"> <BookOpen/> </div>}
                      />
                      <StatCard 
                        title="BOUGHT" 
                        value={stats.buy_count} 
                        icon={() => <div className="text-xs"> <BookLock/> </div>}
                      />
                      <StatCard 
                        title="Fund involved" 
                        value={stats.total_fund_managers} 
                        icon={() => <div className="text-xs"> <Newspaper/>  </div>}
                      />
                  </div>
                </div>

                {/* Transactions Table */}
                <div className="transactions-section">
                <div className="stats-header">
                    <h2 className="stats-title">Transaction</h2>
                    <select className="select-control">
                    <option>All</option>
                    </select>
                </div>
                <Card>
                    <div className="tabs-header">
                    <div className="tabs">
                        <div className="filters-container">

                          {userRole == ROLES.ADMIN ?
                            <>
                              <div className="search-section">
                                <SearchComponent onFundSelect={handleFundSelect} />
                              </div>
                              <div className="search-section">
                                <UserSearch onUserSelect={handleUserSelect} />
                              </div>
                            </>
                            : <></>
                          }

                            <div className="filter-section">
                              <StatusFilterComponent
                                selectedStatus={selectedStatus}
                                onStatusChange={handleStatusChange}
                                statuses={[
                                  { value: 'all', label: 'All Status' },
                                  { value: 'PENDING', label: 'Pending' },
                                  { value: 'COMPLETED', label: 'Completed' },
                                  { value: 'CANCELED', label: 'Canceled' }
                                ]}
                              />
                            </div>

                        </div>
                     </div>
                    </div>
                    <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            {/* <th>Transaction date</th> */}
                            <th>Names</th>
                            <th>Fund</th>
                            <th>Symbol</th>
                            <th>Date</th>
                            <th>Side</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Shares</th>
                        </tr>
                        </thead>
                        <tbody>
                        {transactions.map((transaction, index) => (
                        <tr key={index} onClick={() => goToItem(transaction.transaction_id)}>
                            {/* <td>{transaction.fund_manager_id}</td> */}
                            <td>
                                <div className="stacked-text">
                                  <span>{transaction.user_first_name}</span>
                                  <span>{transaction.user_last_name}</span>
                                </div>
                            </td>
                            <td>
                                <span className="highlight-underline">
                                  {transaction.fund_name}
                                </span>
                            </td>
                            <td>
                              <span className="my-span">
                              {transaction.fund_symbol}
                              </span>
                            </td>
                            <td>{formatDateIntl(transaction.transaction_date)}</td>
                            <td>
                              <span className={`status-badge ${getSideClass(transaction.transaction_type)}`}>
                                 {transaction.transaction_type}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${getStatusClass(transaction.status)}`}>
                                {transaction.status}
                              </span>
                            </td>
                            <td>{formatCurrency(transaction.amount, transaction.currency_code)}</td>
                            <td>{formatCurrency(transaction.shares, '')}</td>
                            {/* <td>{transaction.receiveAmount}</td>
                            <td><span className={`status ${transaction.status.toLowerCase()}`}>{transaction.status}</span></td> */}
                        </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    <div className="pagination">
                    <div>
                        <p className="pagination-text">
                        Showing page {currentPage} of {Math.ceil(total / itemsPerPage)}
                        </p>
                    </div>
                    <div className="pagination-buttons">
                        <button
                        onClick={() => paginate(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        >
                        <ChevronLeft className="icon" />
                        </button>
                        <button
                        onClick={() => paginate(p => p + 1)}
                        disabled={currentPage * itemsPerPage >= total}
                        className="pagination-button"
                        >
                        <ChevronRight className="icon" />
                        </button>
                    </div>
                    </div>
                </Card>
            </div>
        </div>
      }
    />
  );
}

export default TransactionPage;
